// The role items shared by the account menu and Profile (FR-032, FR-033; P-PROF-4): Switch to
// school admin / Switch to parent for a web user holding both served roles (decided through
// effectiveRole), and Refresh access for everyone signed in.
import { useRouter } from "expo-router";

import { AUTH_COPY } from "@/auth/copy";
import { effectiveRole } from "@/session/access";
import { useSession, useSessionController } from "@/session/hooks";
import { usePlatform } from "@/session/platform";
import { servedRoles } from "@/session/store";

import { runGuarded } from "./leaveGuard";

export type RoleAction = { id: string; label: string; onSelect: () => void };

export function useRoleActions(): RoleAction[] {
  const session = useSession();
  const platform = usePlatform();
  const router = useRouter();
  const { switchRole, refreshAccess } = useSessionController();
  if (session.status !== "signedIn") return [];

  const served = servedRoles(session.roles);
  const current = effectiveRole(session, platform);
  const canSwitch =
    platform === "web" &&
    served.includes("PARENT") &&
    served.includes("SCHOOL_ADMIN") &&
    session.schoolAdminSchools.length > 0;

  const actions: RoleAction[] = [];
  if (canSwitch && current === "PARENT") {
    actions.push({
      id: "switch-school-admin",
      label: AUTH_COPY.roles.switchToSchoolAdmin,
      onSelect: () =>
        void switchRole("SCHOOL_ADMIN").then(() => router.replace("/school")),
    });
  } else if (canSwitch && current === "SCHOOL_ADMIN") {
    actions.push({
      id: "switch-parent",
      label: AUTH_COPY.roles.switchToParent,
      // 006 FR-014: unsaved class changes ask first.
      onSelect: () =>
        void runGuarded(() =>
          switchRole("PARENT").then(() => router.replace("/")),
        ),
    });
  }
  actions.push({
    id: "refresh-access",
    label: AUTH_COPY.roles.refreshAccess,
    onSelect: () => void refreshAccess(),
  });
  return actions;
}
